import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import {
  getContactMessages,
  updateContactMessageStatus,
} from "../../helpers/firestoreService";
import { useAdmin } from "../../hooks/useAdmin";

const STATUS_COLORS = {
  new: "primary",
  read: "warning",
  resolved: "success",
};

const AdminContactMessages = () => {
  const { isAdmin, adminLoading } = useAdmin();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    document.title = "Admin — Contact Messages";
  }, []);

  const loadMessages = async () => {
    setLoading(true);
    setError("");
    try {
      setMessages(await getContactMessages());
    } catch (loadError) {
      console.error("Contact inbox load failed:", loadError);
      setError("Contact messages could not be loaded.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!adminLoading && !isAdmin) navigate("/overview");
  }, [adminLoading, isAdmin, navigate]);

  useEffect(() => {
    if (isAdmin) loadMessages();
  }, [isAdmin]);

  const setStatus = async (message, status) => {
    setActing(true);
    try {
      await updateContactMessageStatus(message.id, status);
      const updated = { ...message, status };
      setMessages((current) => current.map((item) => item.id === message.id ? updated : item));
      setSelected((current) => current?.id === message.id ? updated : current);
    } catch (updateError) {
      console.error("Contact message update failed:", updateError);
      setError("The message status could not be updated.");
    } finally {
      setActing(false);
    }
  };

  const openMessage = (message) => {
    setSelected(message);
    if (message.status === "new") setStatus(message, "read");
  };

  if (adminLoading || loading) {
    return <div className="page-content text-center py-5"><Spinner color="primary" /></div>;
  }
  if (!isAdmin) return null;

  const normalizedSearch = search.trim().toLowerCase();
  const filtered = messages.filter((message) => {
    const matchesStatus = filter === "all" || message.status === filter;
    const matchesSearch = !normalizedSearch || [message.name, message.email, message.subject]
      .some((value) => (value || "").toLowerCase().includes(normalizedSearch));
    return matchesStatus && matchesSearch;
  });

  const counts = {
    all: messages.length,
    new: messages.filter((message) => message.status === "new").length,
    read: messages.filter((message) => message.status === "read").length,
    resolved: messages.filter((message) => message.status === "resolved").length,
  };

  return (
    <div className="page-content">
      <Container fluid>
        <div className="rounded mb-4 p-4 text-white position-relative overflow-hidden" style={{ minHeight: 130 }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #4f46e5 0%, #8b5cf6 100%)" }} />
          <div className="position-relative" style={{ zIndex: 1 }}>
            <Row className="align-items-center">
              <Col>
                <h3 className="mb-1 text-white">
                  <i className="bx bx-envelope me-2" />Contact Messages
                </h3>
                <p className="mb-0 opacity-75">Messages submitted through the public Porcher contact page.</p>
              </Col>
              <Col xs="auto">
                <Button color="light" size="sm" onClick={loadMessages}>
                  <i className="bx bx-refresh me-1" /> Refresh
                </Button>
              </Col>
            </Row>
            <Row className="mt-3 g-2">
              {Object.keys(counts).map((status) => (
                <Col xs="auto" key={status}>
                  <Badge
                    color={status === "all" ? "light" : STATUS_COLORS[status]}
                    className={`fs-6 px-3 py-2 ${status === "all" ? "text-dark" : ""}`}
                    style={{ cursor: "pointer", opacity: filter === status ? 1 : 0.68, textTransform: "capitalize" }}
                    onClick={() => setFilter(status)}
                  >
                    {status}: {counts[status]}
                  </Badge>
                </Col>
              ))}
            </Row>
          </div>
        </div>

        {error && <Alert color="danger">{error}</Alert>}

        <Card className="border-0 shadow-sm">
          <CardBody className="border-bottom">
            <Row className="align-items-center g-3">
              <Col md={5}>
                <Input
                  bsSize="sm"
                  placeholder="Search name, email, or subject"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </Col>
              <Col xs="auto" className="ms-auto text-muted small">
                Showing {filtered.length} of {messages.length}
              </Col>
            </Row>
          </CardBody>
          <CardBody className="p-0">
            {filtered.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bx bx-envelope-open" style={{ fontSize: "2.5rem" }} />
                <p className="mt-2 mb-0">No contact messages match this view.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table className="table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Status</th>
                      <th>From</th>
                      <th>Subject</th>
                      <th>Received</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((message) => (
                      <tr key={message.id} className={message.status === "new" ? "fw-semibold" : ""}>
                        <td><Badge color={STATUS_COLORS[message.status] || "secondary"}>{message.status}</Badge></td>
                        <td>
                          <div>{message.name}</div>
                          <small className="text-muted">{message.email}</small>
                        </td>
                        <td>{message.subject}</td>
                        <td>{message.createdAt ? message.createdAt.toLocaleString() : "Pending"}</td>
                        <td className="text-end">
                          <Button color="primary" size="sm" outline onClick={() => openMessage(message)}>
                            <i className="bx bx-show me-1" /> View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>

      <Modal isOpen={Boolean(selected)} toggle={() => setSelected(null)} centered size="lg">
        {selected && (
          <>
            <ModalHeader toggle={() => setSelected(null)}>{selected.subject}</ModalHeader>
            <ModalBody>
              <div className="d-flex flex-wrap justify-content-between gap-2 mb-4">
                <div>
                  <div className="fw-semibold">{selected.name}</div>
                  <a href={`mailto:${selected.email}`}>{selected.email}</a>
                </div>
                <div className="text-end">
                  <Badge color={STATUS_COLORS[selected.status] || "secondary"}>{selected.status}</Badge>
                  <div className="text-muted small mt-1">
                    {selected.createdAt ? selected.createdAt.toLocaleString() : "Pending timestamp"}
                  </div>
                </div>
              </div>
              <div className="bg-light border rounded p-3" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                {selected.message}
              </div>
            </ModalBody>
            <ModalFooter>
              {selected.status !== "resolved" && (
                <Button color="success" onClick={() => setStatus(selected, "resolved")} disabled={acting}>
                  <i className="bx bx-check me-1" /> Mark Resolved
                </Button>
              )}
              <Button color="primary" tag="a" href={`mailto:${selected.email}?subject=${encodeURIComponent(`Re: ${selected.subject}`)}`}>
                <i className="bx bx-reply me-1" /> Reply by Email
              </Button>
              <Button color="secondary" outline onClick={() => setSelected(null)}>Close</Button>
            </ModalFooter>
          </>
        )}
      </Modal>
    </div>
  );
};

export default AdminContactMessages;